import { Button, Card, Code, Divider, Group, Stack, Text } from "@mantine/core";
import React, { useMemo, useState } from "react";
import { IReportChapterNode } from "@mcapps/shared/reportSchemas/kapitelSchema";
import { toggleChapterInTree } from "./reportChaptersTreeUtils";
import { ReportChaptersTree } from "./ReportChapterTree";

const DEMO_INIT: IReportChapterNode[] = [
  {
    id: "1",
    title: "Innledning",
    text: "1: Innledning",
    isActive: true,
    children: [
      {
        id: "1.1",
        title: "Formelle forhold",
        text: "1.1: Formelle forhold",
        isActive: true,
        children: [
          {
            id: "1.1.1",
            title: "Avgrensning",
            text: "1.1.1: Avgrensning",
            isActive: true,
          },
        ],
      },
      {
        id: "1.2",
        title: "Definisjoner",
        text: "1.2: Definisjoner",
        isActive: true,
      },
    ],
  },
  {
    id: "2",
    title: "Myndighetskrav, prosjektkrav og grensesnitt",
    text: "2: Myndighetskrav, prosjektkrav og grensesnitt",
    isActive: true,
    isDisabled: true,
    children: [
      {
        id: "2.1",
        title: "Forskrifter",
        text: "2.1: Forskrifter",
        isActive: true,
        isDisabled: true,
        children: [
          {
            id: "2.1.1",
            title: "Relevante standarder",
            text: "2.1.1: Relevante standarder",
            isActive: true,
            isDisabled: true,
          },
          {
            id: "2.1.2",
            title: "Dokumentasjon",
            text: "2.1.2: Dokumentasjon",
            isActive: true,
            isDisabled: true,
          },
        ],
      },
      {
        id: "2.2",
        title: "Prosjektspesifikke krav ut over forskriftskrav",
        text: "2.2: Prosjektspesifikke krav ut over forskriftskrav",
        isActive: true,
      },
    ],
  },
];

// enkel deep clone så reset inte delar referenser
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

export const ReportChaptersTreeDemo: React.FC = () => {
  const initial = useMemo(() => clone(DEMO_INIT), []);
  const [chapters, setChapters] = useState<IReportChapterNode[]>(initial);

  const handleToggle = (chapterId: string, next: boolean) => {
    setChapters((prev) => toggleChapterInTree(prev, chapterId, next));
    // Om din util fortfarande tar "mode":
    // setChapters((prev) => toggleChapterInTree(prev, chapterId, next, "single"));
  };

  const reset = () => setChapters(clone(DEMO_INIT));

  return (
    <Stack gap="md">
      <Card withBorder radius="md" p="md">
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Text fw={700}>ReportChaptersTree – Demo</Text>

            <Button variant="light" onClick={reset}>
              Reset
            </Button>
          </Group>

          <Text size="sm" c="dimmed">
            Ny logikk for kapitler:
            <br />• Avkrysningsbokser har kun to tilstander: <b>på</b> (checked) og <b>av</b> (unchecked).
            <br />• Setter du et <b>kapittel</b> til <b>på</b>, blir alle underkapitler automatisk satt til <b>på</b>.
            <br />• Setter du et <b>underkapittel</b> til <b>på</b>, blir alle overordnede kapitler automatisk satt til{" "}
            <b>på</b>.
            <br />• Setter du et kapittel eller underkapittel til <b>av</b>, påvirker dette kun den valgte noden –
            overordnede kapitler forblir uendret.
          </Text>
        </Stack>
      </Card>

      <Card withBorder radius="md" p="md">
        <ReportChaptersTree
          titleNode="Kapitler"
          chapters={chapters}
          onToggle={handleToggle}
          toggleDescendantsOnParentToggle={false}
          renderLabel={(n) => <span>{n.text ?? n.title}</span>}
          hideEmptyChildren
          isDisabled={(n) => n.isDisabled === true}
        />
      </Card>

      <Card withBorder radius="md" p="md">
        <Stack gap="xs">
          <Text fw={700}>Debug (state)</Text>
          <Divider />
          <Code block>{JSON.stringify(chapters, null, 2)}</Code>
        </Stack>
      </Card>
    </Stack>
  );
};
